import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  CameraIcon,
  HomeIcon,
  ImageIcon,
  PencilIcon,
  PlusIcon,
  UserIcon,
} from 'lucide-react'
import { StringItem } from '../App'
import { changePassword } from '../api/auth'
import {
  updateProfile,
  uploadCoverImage,
  uploadProfileImage,
} from '../api/profile'
import { stringPublicToItem } from '../utils/stringItem'
import { formatMemberSince } from '../utils/format'
import {
  isSessionExpiredMessage,
  redirectIfSessionExpired,
  redirectToLoginForExpiredSession,
} from '../utils/sessionAuth'
import { BadgeModal } from './BadgeModal'
import { getBadgeById, PROFILE_BADGE_ID } from '../data/badges'
import { useMe, useUserProfile, useMyStrings, useUserStrings } from '../api/hooks'
import { NewStringModal } from './NewStringModal'

type ProfileTab = 'strings' | 'details'

const DEFAULT_AVATAR_SRC = '/red_eye.png'

function cacheBustUrl(url: string): string {
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}v=${Date.now()}`
}

interface ProfilePageProps {
  onSelectString: (s: StringItem) => void
}

function DetailRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {

  
  return (
    <div className="space-y-2 py-6 border-b border-white/10 first:pt-0">
      <p className="text-[11px] text-white/45 tracking-widest uppercase">
        {label}
      </p>
      <div className="text-sm md:text-base text-white/95 tracking-wide break-words">
        {children}
      </div>
    </div>
  )
}

export function ProfilePage({ onSelectString }: ProfilePageProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [tab, setTab] = useState<ProfileTab>('strings')
  const [activeBadgeId, setActiveBadgeId] = useState<string | null>(null)
  const [newStringOpen, setNewStringOpen] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const [uploadingProfile, setUploadingProfile] = useState(false)
  const [uploadingCover, setUploadingCover] = useState(false)
  const [coverImageBroken, setCoverImageBroken] = useState(false)
  const [profileImageBroken, setProfileImageBroken] = useState(false)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [passwordSubmitting, setPasswordSubmitting] = useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [editingBio, setEditingBio] = useState(false)
  const [bioDraft, setBioDraft] = useState('')
  const [savingBio, setSavingBio] = useState(false)
  const [bioError, setBioError] = useState('')
  const [emailPrefsSaving, setEmailPrefsSaving] = useState(false)
  const [emailPrefsError, setEmailPrefsError] = useState('')
  const profileInputRef = useRef<HTMLInputElement>(null)
  const coverInputRef = useRef<HTMLInputElement>(null)
  const viewUsername = searchParams.get('user')?.trim().replace(/^@/, '') || ''
  const isPublicProfile = Boolean(viewUsername)

  // SWR-backed profile + strings data
  const { data: me, error: meErr, isLoading: meLoading, mutate: mutateMe } = useMe(!isPublicProfile)
  const { data: publicUser, error: pubErr, isLoading: pubLoading, mutate: mutatePub } = useUserProfile(isPublicProfile ? viewUsername : '')
  const { data: myStringsData, isLoading: myStrLoading, mutate: reloadMyStrings } = useMyStrings(!isPublicProfile)
  const { data: userStringsData, isLoading: userStrLoading } = useUserStrings(isPublicProfile ? viewUsername : '')

  const user = isPublicProfile ? publicUser : me
  const mutateProfile = isPublicProfile ? mutatePub : mutateMe
  const rawStrings = isPublicProfile ? userStringsData : myStringsData
  const loading = isPublicProfile ? (pubLoading || userStrLoading) : (meLoading || myStrLoading)
  const swrError = isPublicProfile ? pubErr : meErr
  const error = swrError instanceof Error ? swrError.message : swrError ? 'Could not load profile.' : ''
  const userStrings = useMemo(() => (rawStrings ?? []).map(stringPublicToItem), [rawStrings])
  const emailNotificationsEnabled =
    user?.settings?.emailNotifications?.enabled ?? true

  useEffect(() => {
    if (searchParams.get('tab') === 'details') {
      setTab('details')
    }
  }, [searchParams])

  useEffect(() => {
    if (isPublicProfile || loading || !isSessionExpiredMessage(error)) return
    redirectToLoginForExpiredSession(navigate)
  }, [error, isPublicProfile, loading, navigate])

  // Reset UI state when profile target changes
  useEffect(() => {
    setUploadError('')
    setEditingBio(false)
    setShowPasswordForm(false)
  }, [isPublicProfile, viewUsername])

  useEffect(() => {
    setCoverImageBroken(false)
  }, [user?.coverImageUrl])

  useEffect(() => {
    setProfileImageBroken(false)
  }, [user?.profileImageUrl])

  const activeBadge = activeBadgeId ? getBadgeById(activeBadgeId) : null
  const primaryBadgeId = user?.badges?.[0] ?? PROFILE_BADGE_ID
  const primaryBadge = getBadgeById(primaryBadgeId)

  const displayName = user?.displayName?.trim() || user?.username || '—'
  const username = user ? `@${user.username}` : '—'
  const bioText = user?.bio?.trim() ?? ''
  const bioDisplay =
    bioText || 'No bio yet — tap edit to add something about yourself.'
  const displayNameClass =
    displayName.length > 32
      ? 'text-xl md:text-2xl lg:text-3xl tracking-wide'
      : displayName.length > 20
        ? 'text-2xl md:text-3xl lg:text-4xl tracking-wider'
        : 'text-3xl md:text-4xl lg:text-5xl tracking-widest'
  const usernameStyle: React.CSSProperties = {
    fontSize: '11.9px',
    letterSpacing: '0.1em',
    marginRight: '15px',
  }
  const bioClass =
    bioDisplay.length > 260
      ? 'text-xs md:text-sm tracking-wide'
      : bioDisplay.length > 140
        ? 'text-[13px] md:text-sm tracking-wider'
        : 'text-sm md:text-base tracking-wider'
  const avatarSrc =
    user?.profileImageUrl && !profileImageBroken
      ? user.profileImageUrl
      : DEFAULT_AVATAR_SRC
  const coverSrc =
    user?.coverImageUrl && !coverImageBroken ? user.coverImageUrl : null

  const handleProfileFile = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setUploadError('')
    setUploadingProfile(true)
    try {
      const updated = await uploadProfileImage(file)
      mutateProfile(
        { ...updated, profileImageUrl: updated.profileImageUrl ? cacheBustUrl(updated.profileImageUrl) : null },
        { revalidate: false },
      )
    } catch (err) {
      if (redirectIfSessionExpired(navigate, err)) return
      setUploadError(
        err instanceof Error ? err.message : 'Could not update profile photo.',
      )
    } finally {
      setUploadingProfile(false)
    }
  }

  const resetPasswordForm = () => {
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setPasswordError('')
  }

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault()
    setPasswordError('')
    setPasswordSuccess('')

    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match.')
      return
    }

    setPasswordSubmitting(true)
    try {
      await changePassword({
        currentPassword,
        newPassword,
      })
      resetPasswordForm()
      setShowPasswordForm(false)
      setPasswordSuccess('Password updated.')
    } catch (err) {
      if (redirectIfSessionExpired(navigate, err)) return
      setPasswordError(
        err instanceof Error ? err.message : 'Could not change password.',
      )
    } finally {
      setPasswordSubmitting(false)
    }
  }

  const startBioEdit = () => {
    setBioDraft(bioText)
    setBioError('')
    setEditingBio(true)
  }

  const cancelBioEdit = () => {
    setBioDraft('')
    setBioError('')
    setEditingBio(false)
  }

  const handleSaveBio = async () => {
    setBioError('')
    setSavingBio(true)
    try {
      const updated = await updateProfile({ bio: bioDraft })
      mutateProfile(updated, { revalidate: false })
      setEditingBio(false)
    } catch (err) {
      if (redirectIfSessionExpired(navigate, err)) return
      setBioError(
        err instanceof Error ? err.message : 'Could not update bio.',
      )
    } finally {
      setSavingBio(false)
    }
  }

  const handleEmailNotificationsChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const enabled = event.target.checked
    setEmailPrefsError('')
    setEmailPrefsSaving(true)
    try {
      const updated = await updateProfile({ emailNotificationsEnabled: enabled })
      mutateProfile(updated, { revalidate: false })
    } catch (err) {
      if (redirectIfSessionExpired(navigate, err)) return
      setEmailPrefsError(
        err instanceof Error
          ? err.message
          : 'Could not update email preferences.',
      )
    } finally {
      setEmailPrefsSaving(false)
    }
  }

  const handleCoverFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setUploadError('')
    setUploadingCover(true)
    try {
      const updated = await uploadCoverImage(file)
      setCoverImageBroken(false)
      mutateProfile(
        { ...updated, coverImageUrl: updated.coverImageUrl ? cacheBustUrl(updated.coverImageUrl) : null },
        { revalidate: false },
      )
    } catch (err) {
      if (redirectIfSessionExpired(navigate, err)) return
      setUploadError(
        err instanceof Error ? err.message : 'Could not update cover image.',
      )
    } finally {
      setUploadingCover(false)
    }
  }

  const handleStringPosted = () => {
    reloadMyStrings()
    mutateProfile()
  }

  return (
    <div className="min-h-screen text-white font-mono select-none relative pb-32">
      <input
        ref={profileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        tabIndex={-1}
        aria-hidden
        onChange={handleProfileFile}
      />
      <input
        ref={coverInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        tabIndex={-1}
        aria-hidden
        onChange={handleCoverFile}
      />

      <div className="fixed inset-0 z-[-1] pointer-events-none bg-black">
        {coverSrc && (
          <img
            src={coverSrc}
            alt=""
            className="w-full h-full object-cover blur-md"
            onError={() => {
              if (user?.coverImageUrl) {
                setCoverImageBroken(true)
                setUploadError(
                  'Cover is saved but the browser cannot load it. In Azure Portal, set the gutstring container access level to Blob (anonymous read).',
                )
              }
            }}
          />
        )}
        <motion.div className="absolute inset-0 bg-black/30" />
      </div>

      {!loading && !error && user && !isPublicProfile && (
        <button
          type="button"
          onClick={() => coverInputRef.current?.click()}
          disabled={uploadingCover}
          className="absolute top-20 right-4 z-30 flex items-center gap-2 px-3 py-2 rounded-full bg-black/50 backdrop-blur-md border border-white/20 text-[10px] uppercase tracking-widest text-white/90 hover:bg-black/65 hover:border-white/35 transition-colors disabled:opacity-50 pointer-events-auto"
          aria-label="Change cover image"
        >
          <ImageIcon className="w-3.5 h-3.5 shrink-0" />
          <span>{uploadingCover ? 'Uploading…' : 'Change cover'}</span>
        </button>
      )}

      <nav className="sticky top-0 z-40 bg-white/5 backdrop-blur-xl border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Link
            to="/"
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            aria-label="Discover"
          >
            <HomeIcon className="w-4 h-4 text-white/70" />
          </Link>
          <span className="text-sm tracking-widest uppercase">GUTSTRING</span>
          <div className="flex items-center space-x-1 text-white/50">
            <UserIcon className="w-3 h-3" />
            <span className="text-xs uppercase tracking-widest">PROFILE</span>
          </div>
        </div>
        {!isPublicProfile && (
          <button
            type="button"
            onClick={() => setNewStringOpen(true)}
            className="flex items-center space-x-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/15 hover:bg-white/20 transition-colors rounded-full text-xs uppercase tracking-widest"
          >
            <PlusIcon className="w-3 h-3" />
            <span>NEW STRING</span>
          </button>
        )}
      </nav>

      <AnimatePresence>
        {newStringOpen && (
          <NewStringModal
            onClose={() => setNewStringOpen(false)}
            onPosted={handleStringPosted}
          />
        )}
      </AnimatePresence>

      <main className="max-w-3xl mx-auto px-4 pt-12 md:pt-24">
        {loading && (
          <p className="text-center text-white/50 text-sm tracking-widest uppercase py-16">
            Loading profile…
          </p>
        )}

        {!loading && error && (
          <motion.div className="text-center py-16 space-y-4">
            <p className="text-white/70 text-sm tracking-wide">{error}</p>
            {error.toLowerCase().includes('log in') ? (
              <Link
                to="/login"
                className="inline-block text-xs tracking-widest uppercase underline text-white/90"
              >
                Log in
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => mutateProfile()}
                className="text-xs tracking-widest uppercase underline text-white/70 hover:text-white"
              >
                Try again
              </button>
            )}
          </motion.div>
        )}

        {!loading && !error && user && (
          <>
            {uploadError && (
              <p className="text-center text-rose-300/90 text-xs tracking-wide mb-6">
                {uploadError}
              </p>
            )}

            <motion.div className="flex flex-col items-end text-right mb-16 space-y-5">
              <motion.div
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35 }}
                className="relative w-24 h-24 md:w-28 md:h-28 shrink-0 group"
              >
                <div className="w-full h-full rounded-full overflow-hidden border border-white/25 ring-2 ring-white/10">
                  <img
                    src={avatarSrc}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={() => {
                      if (user?.profileImageUrl) {
                        setProfileImageBroken(true)
                        setUploadError(
                          'Profile photo is saved but cannot be displayed. Set the gutstring container to Blob (anonymous read) in Azure Portal.',
                        )
                      }
                    }}
                  />
                </div>
                {!isPublicProfile && (
                  <button
                    type="button"
                    onClick={() => profileInputRef.current?.click()}
                    disabled={uploadingProfile}
                    className="absolute bottom-0 right-0 flex items-center justify-center w-9 h-9 rounded-full bg-white/15 backdrop-blur-md border border-white/30 text-white hover:bg-white/25 hover:border-white/45 transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                    aria-label="Change profile photo"
                  >
                    <CameraIcon className="w-4 h-4" />
                  </button>
                )}
                {uploadingProfile && (
                  <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-widest text-white/50 whitespace-nowrap">
                    Uploading…
                  </span>
                )}
              </motion.div>
              <motion.div
                className="flex w-full max-w-full flex-col items-end gap-1.5"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <h1 className={`${displayNameClass} max-w-full break-words uppercase leading-tight cursor-default`}>
                  {displayName}
                </h1>
                <p
                  className="max-w-full whitespace-nowrap text-white/55 cursor-default"
                  style={usernameStyle}
                >
                  {username}
                </p>
                {primaryBadge && (
                  <motion.button
                    type="button"
                    onClick={() => setActiveBadgeId(primaryBadge.id)}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1, duration: 0.35 }}
                    whileHover={{ scale: 1.06 }}
                    whileTap={{ scale: 0.96 }}
                    className="cursor-pointer rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                    aria-label={`View ${primaryBadge.title} badge details`}
                  >
                    <img
                      src={primaryBadge.imageSrc}
                      alt={primaryBadge.imageAlt}
                      className="w-auto max-w-[100px] md:max-w-[120px] h-auto drop-shadow-md pointer-events-none"
                    />
                  </motion.button>
                )}
              </motion.div>
              <div className="relative max-w-md pt-2 w-full">
                {editingBio ? (
                  <div className="space-y-3 pr-0">
                    <textarea
                      value={bioDraft}
                      onChange={(e) => setBioDraft(e.target.value)}
                      maxLength={500}
                      rows={4}
                      className="w-full resize-y select-text bg-white/10 border border-white/25 rounded-xl px-3 py-2.5 text-sm text-white/95 leading-relaxed normal-case tracking-normal placeholder:text-white/40 focus:outline-none focus:border-white/45"
                      placeholder="Write something about yourself…"
                      aria-label="Bio"
                    />
                    <p className="text-[10px] text-white/40 tracking-widest uppercase text-right">
                      {bioDraft.length}/500
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={handleSaveBio}
                        disabled={savingBio}
                        className="px-4 py-2 rounded-full bg-white/15 border border-white/30 text-[10px] uppercase tracking-widest text-white hover:bg-white/25 transition-colors disabled:opacity-50"
                      >
                        {savingBio ? 'Saving…' : 'Save bio'}
                      </button>
                      <button
                        type="button"
                        onClick={cancelBioEdit}
                        disabled={savingBio}
                        className="px-4 py-2 rounded-full border border-white/20 text-[10px] uppercase tracking-widest text-white/70 hover:text-white hover:border-white/35 transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                    {bioError && (
                      <p className="text-xs text-rose-300/90 normal-case tracking-normal">
                        {bioError}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="relative pr-12">
                    <p className={`${bioClass} text-white/90 leading-relaxed uppercase whitespace-pre-wrap break-words cursor-default`}>
                      {bioDisplay}
                    </p>
                    {!isPublicProfile && (
                      <button
                        type="button"
                        onClick={startBioEdit}
                        className="absolute top-0 right-0 flex items-center justify-center w-9 h-9 rounded-full bg-white/15 backdrop-blur-md border border-white/30 text-white hover:bg-white/25 hover:border-white/45 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
                        aria-label="Edit bio"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </div>
              {user.stats && (
                <p className="text-[10px] text-white/45 tracking-widest uppercase">
                  {user.stats.strings} string{user.stats.strings === 1 ? '' : 's'}
                </p>
              )}
            </motion.div>

            <AnimatePresence>
              {activeBadge && (
                <BadgeModal
                  badge={activeBadge}
                  onClose={() => setActiveBadgeId(null)}
                />
              )}
            </AnimatePresence>

            <div className="border-b border-white/10 mb-8 flex gap-8 sm:gap-10">
              <button
                type="button"
                onClick={() => {
                  setTab('strings')
                  if (!isPublicProfile && searchParams.get('tab')) {
                    const next = new URLSearchParams(searchParams)
                    next.delete('tab')
                    const qs = next.toString()
                    window.history.replaceState(
                      null,
                      '',
                      qs
                        ? `${window.location.pathname}?${qs}`
                        : window.location.pathname,
                    )
                  }
                }}
                className={`pb-4 text-sm uppercase tracking-widest transition-colors border-b-2 -mb-px ${
                  tab === 'strings'
                    ? 'text-white border-white'
                    : 'text-white/45 border-transparent hover:text-white/80'
                }`}
              >
                STRINGS
              </button>
              <button
                type="button"
                onClick={() => {
                  setTab('details')
                  if (!isPublicProfile) {
                    const next = new URLSearchParams(searchParams)
                    next.set('view', 'profile')
                    next.set('tab', 'details')
                    window.history.replaceState(
                      null,
                      '',
                      `${window.location.pathname}?${next.toString()}`,
                    )
                  }
                }}
                className={`pb-4 text-sm uppercase tracking-widest transition-colors border-b-2 -mb-px ${
                  tab === 'details'
                    ? 'text-white border-white'
                    : 'text-white/45 border-transparent hover:text-white/80'
                }`}
              >
                DETAILS
              </button>
            </div>

            {tab === 'strings' && (
              <div className="space-y-6">
                {userStrings.length === 0 && (
                  <p className="text-center text-white/45 text-sm tracking-widest uppercase py-12">
                    {isPublicProfile
                      ? 'No strings yet.'
                      : 'No strings yet — post your first one with New String.'}
                  </p>
                )}
                {userStrings.map((string) => (
                  <motion.div
                    key={string.id}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: 'spring', stiffness: 380, damping: 28 }}
                    onClick={() => onSelectString(string)}
                    className="bg-white/5 backdrop-blur-md border border-white/30 rounded-3xl p-8 md:p-10 hover:bg-white/10 hover:border-white/40 transition-colors cursor-pointer shadow-lg"
                  >
                    <p className="text-xl md:text-2xl leading-relaxed mb-8 whitespace-pre-wrap uppercase tracking-wide">
                      {string.content}
                    </p>
                    <div className="flex justify-between items-center text-xs md:text-sm text-white/60 tracking-widest uppercase">
                      <span>{string.author}</span>
                      <span>
                        {string.time}
                        {string.date ? ` · ${string.date}` : ''}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {tab === 'details' && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="max-w-lg bg-white/5 backdrop-blur-md border border-white/15 rounded-3xl px-6 md:px-8 pt-4 pb-6 mb-12"
              >
                <DetailRow label="Username">{username}</DetailRow>
                <DetailRow label="Display name">{displayName}</DetailRow>
                <DetailRow label="Bio">
                  <span className="whitespace-pre-wrap normal-case">{user.bio || '—'}</span>
                </DetailRow>
                <DetailRow label="Member since">
                  {formatMemberSince(user.createdAt) || '—'}
                </DetailRow>
                {!isPublicProfile && (
                  <DetailRow label="Email notifications">
                    <div className="space-y-2 normal-case">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={emailNotificationsEnabled}
                          disabled={emailPrefsSaving}
                          onChange={handleEmailNotificationsChange}
                          className="mt-1 shrink-0"
                        />
                        <span className="text-sm text-white/85 leading-relaxed">
                          Activity and digest updates to your account email
                        </span>
                      </label>
                      <p className="text-xs text-white/45 leading-relaxed pl-7">
                        On by default. Turn off anytime — link in email footers
                        opens this tab.
                      </p>
                      {emailPrefsError && (
                        <p className="text-xs text-rose-300/90">{emailPrefsError}</p>
                      )}
                    </div>
                  </DetailRow>
                )}
                {!isPublicProfile && (
                  <DetailRow label="Password">
                    <div className="space-y-3 normal-case">
                      {!showPasswordForm ? (
                        <button
                          type="button"
                          onClick={() => {
                            setShowPasswordForm(true)
                            setPasswordSuccess('')
                            setPasswordError('')
                          }}
                          className="text-xs uppercase tracking-widest text-white/80 underline hover:text-white"
                        >
                          Change password
                        </button>
                      ) : (
                        <form onSubmit={handleChangePassword} className="space-y-3">
                          <label className="block space-y-1.5">
                            <span className="text-[10px] text-white/45 tracking-widest uppercase">
                              Current password
                            </span>
                            <input
                              type="password"
                              autoComplete="current-password"
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              required
                              className="w-full select-text px-3 py-2 rounded-lg bg-black/30 border border-white/15 text-sm text-white focus:outline-none focus:border-white/35"
                            />
                          </label>
                          <label className="block space-y-1.5">
                            <span className="text-[10px] text-white/45 tracking-widest uppercase">
                              New password
                            </span>
                            <input
                              type="password"
                              autoComplete="new-password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              required
                              minLength={8}
                              className="w-full select-text px-3 py-2 rounded-lg bg-black/30 border border-white/15 text-sm text-white focus:outline-none focus:border-white/35"
                            />
                          </label>
                          <label className="block space-y-1.5">
                            <span className="text-[10px] text-white/45 tracking-widest uppercase">
                              Confirm new password
                            </span>
                            <input
                              type="password"
                              autoComplete="new-password"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              required
                              minLength={8}
                              className="w-full select-text px-3 py-2 rounded-lg bg-black/30 border border-white/15 text-sm text-white focus:outline-none focus:border-white/35"
                            />
                          </label>
                          <div className="flex flex-wrap gap-3 pt-1">
                            <button
                              type="submit"
                              disabled={passwordSubmitting}
                              className="px-4 py-2 rounded-full bg-white/15 border border-white/25 text-[10px] uppercase tracking-widest hover:bg-white/25 disabled:opacity-50"
                            >
                              {passwordSubmitting ? 'Saving…' : 'Save password'}
                            </button>
                            <button
                              type="button"
                              disabled={passwordSubmitting}
                              onClick={() => {
                                setShowPasswordForm(false)
                                resetPasswordForm()
                              }}
                              className="px-4 py-2 text-[10px] uppercase tracking-widest text-white/55 hover:text-white/85"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      )}
                      {passwordError && (
                        <p className="text-xs text-rose-300/90 tracking-wide">
                          {passwordError}
                        </p>
                      )}
                      {passwordSuccess && (
                        <p className="text-xs text-emerald-300/90 tracking-wide">
                          {passwordSuccess}
                        </p>
                      )}
                    </div>
                  </DetailRow>
                )}
              </motion.div>
            )}
          </>
        )}
      </main>
    </div>
  )
}
